// Control segmentado sobre `.seg-toggle` (auth.css): Mes / Semana,
// Todas / específicas, Mañana / Tarde / Hora exacta, la vigencia de un
// documento. Semántica de radiogroup, la misma que SegRadio en
// routes/DetalleProyecto.tsx: una sola opción elegida, flechas (e
// Inicio/Fin) eligen y mueven el foco, Tabulador entra a la opción
// elegida y sale del grupo. Los otros dos .seg-toggle del repo
// (Overview, Observaciones) son pares de botones sin estado anunciado;
// este componente sigue al que sí lo anuncia.

import type { KeyboardEvent } from "react";

export type SegOption<T extends string> = {
  value: T;
  label: string;
  /** Frase del title; por defecto el label. */
  title?: string;
};

export type SegProps<T extends string> = {
  value: T;
  options: readonly SegOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
} & ({ label: string; labelledBy?: never } | { labelledBy: string; label?: never });

export function Seg<T extends string>({
  value,
  options,
  onChange,
  disabled = false,
  className,
  label,
  labelledBy,
}: SegProps<T>) {
  // Si `value` no coincide con ninguna opción, la primera recibe el
  // foco del Tabulador para que el grupo siga siendo alcanzable.
  const selectedIdx = Math.max(
    0,
    options.findIndex((o) => o.value === value)
  );

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (disabled || options.length === 0) return;
    let next: number | null = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (selectedIdx + 1) % options.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp")
      next = (selectedIdx - 1 + options.length) % options.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = options.length - 1;
    if (next === null) return;
    e.preventDefault();
    const option = options[next];
    if (!option) return;
    onChange(option.value);
    e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus();
  }

  return (
    <div
      className={className ? `seg-toggle ${className}` : "seg-toggle"}
      role="radiogroup"
      aria-label={label}
      aria-labelledby={labelledBy}
      aria-disabled={disabled || undefined}
      onKeyDown={handleKeyDown}
    >
      {options.map((o, i) => {
        const checked = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={i === selectedIdx ? 0 : -1}
            title={o.title ?? o.label}
            disabled={disabled}
            className={checked ? "seg-toggle__btn seg-toggle__btn--active" : "seg-toggle__btn"}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
